(() => {
  const ELEMENT_NAME = 'nuora-pdp-390';

  if (customElements.get(ELEMENT_NAME)) return;

  const toNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const openCart = (response, trigger, cartUrl) => {
    if (window.NuoraUpcart && typeof window.NuoraUpcart.open === 'function') {
      window.NuoraUpcart.open();
      return;
    }

    if (typeof window.upcartOpenCart === 'function') {
      try {
        if (typeof window.upcartRefreshCart === 'function') {
          Promise.resolve(window.upcartRefreshCart()).catch(() => {});
        }
        window.upcartOpenCart();
        return;
      } catch (error) {
        // Continue to the native theme drawer.
      }
    }

    const drawer = document.querySelector('cart-drawer');
    if (drawer && response?.sections && typeof drawer.renderContents === 'function') {
      if (typeof drawer.setActiveElement === 'function') {
        drawer.setActiveElement(trigger || document.activeElement);
      }
      drawer.renderContents(response);
      return;
    }

    if (drawer && typeof drawer.open === 'function') {
      drawer.open(trigger || document.activeElement);
      return;
    }

    window.location.assign(cartUrl || window.routes?.cart_url || '/cart');
  };

  class NuoraPdp390 extends HTMLElement {
    connectedCallback() {
      if (this.dataset.n390Ready === 'true') return;
      this.dataset.n390Ready = 'true';

      this.mediaSlides = Array.from(this.querySelectorAll('[data-n390-media-slide]'));
      this.mediaTrack = this.querySelector('[data-n390-media]');
      this.mediaThumbs = Array.from(this.querySelectorAll('[data-n390-media-target]'));
      this.reviewSlides = Array.from(this.querySelectorAll('[data-n390-review]'));
      this.reviewTrack = this.querySelector('[data-n390-review-track]');
      this.reviewDots = Array.from(this.querySelectorAll('[data-n390-review-dot]'));
      this.tiers = Array.from(this.querySelectorAll('[data-n390-tier]'));
      this.faqExtras = Array.from(this.querySelectorAll('[data-n390-faq-extra]'));
      this.faqMoreButton = this.querySelector('[data-n390-faq-more]');
      this.lastInputMethod = 'pointer';
      this.nutritionReturnFocus = null;
      this.mediaIndex = Math.max(0, this.mediaSlides.findIndex((slide) => slide.classList.contains('is-active')));
      this.reviewIndex = Math.max(0, this.reviewSlides.findIndex((slide) => slide.classList.contains('is-active')));

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));
      this.addEventListener('pointerdown', () => { this.lastInputMethod = 'pointer'; }, { passive: true });
      this.addEventListener('keydown', () => { this.lastInputMethod = 'keyboard'; });
      this.mediaTrack?.addEventListener('scroll', this.handleMediaScroll.bind(this), { passive: true });
      this.reviewTrack?.addEventListener('scroll', this.handleReviewScroll.bind(this), { passive: true });
      this.nutritionModal?.addEventListener('close', () => this.restoreNutritionFocus());

      this.activateMedia(this.mediaIndex, false);
      this.activateReview(this.reviewIndex);
      this.ensureSelectedTier();
      this.updateOffer();
      if (this.faqMoreButton) this.faqMoreButton.hidden = this.faqExtras.length === 0;
    }

    get selectedTier() {
      return this.tiers.find((tier) => tier.querySelector('input[type="radio"]')?.checked) || this.tiers[0] || null;
    }

    get refillEnabled() {
      return this.querySelector('[data-n390-refill]')?.getAttribute('aria-pressed') === 'true';
    }

    ensureSelectedTier() {
      if (!this.tiers.length || this.selectedTier) return;
      const input = this.tiers[0].querySelector('input[type="radio"]');
      if (input) input.checked = true;
    }

    handleChange(event) {
      const input = event.target.closest('[data-n390-tier] input[type="radio"]');
      if (!input) return;
      this.updateOffer();
    }

    handleClick(event) {
      const faqMoreButton = event.target.closest('[data-n390-faq-more]');
      if (faqMoreButton) {
        this.toggleMoreFaqs(faqMoreButton);
        return;
      }

      const mediaTarget = event.target.closest('[data-n390-media-target]');
      if (mediaTarget) {
        this.activateMedia(toNumber(mediaTarget.dataset.n390MediaTarget));
        return;
      }

      if (event.target.closest('[data-n390-media-prev]')) {
        this.activateMedia(this.mediaIndex - 1);
        return;
      }

      if (event.target.closest('[data-n390-media-next]')) {
        this.activateMedia(this.mediaIndex + 1);
        return;
      }

      const refill = event.target.closest('[data-n390-refill]');
      if (refill) {
        const nextState = refill.getAttribute('aria-pressed') !== 'true';
        refill.setAttribute('aria-pressed', String(nextState));
        this.updateOffer();
        return;
      }

      if (event.target.closest('[data-n390-atc]')) {
        this.addToCart();
        return;
      }

      if (event.target.closest('[data-n390-review-prev]')) {
        this.activateReview(this.reviewIndex - 1);
        return;
      }

      if (event.target.closest('[data-n390-review-next]')) {
        this.activateReview(this.reviewIndex + 1);
        return;
      }

      const reviewDot = event.target.closest('[data-n390-review-dot]');
      if (reviewDot) {
        this.activateReview(toNumber(reviewDot.dataset.n390ReviewDot));
        return;
      }

      if (event.target.closest('[data-n390-nutrition-open]')) {
        this.openNutrition();
        return;
      }

      if (event.target.closest('[data-n390-nutrition-close]')) {
        this.closeNutrition();
      }
    }

    toggleMoreFaqs(button) {
      const expanded = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(expanded));
      this.faqExtras.forEach((item) => {
        item.hidden = !expanded;
        if (!expanded) item.open = false;
      });
      const label = button.querySelector('[data-n390-faq-more-label]');
      if (label) {
        label.textContent = expanded
          ? button.dataset.lessLabel || 'Show less FAQ'
          : button.dataset.moreLabel || 'Read more FAQ';
      }
    }

    setMediaState(index) {
      this.mediaIndex = index;
      this.mediaSlides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if (!active) {
          slide.querySelectorAll('video').forEach((video) => {
            try {
              video.pause();
            } catch (error) {
              // Media pausing is best-effort.
            }
          });
        }
      });

      this.mediaThumbs.forEach((thumb) => {
        const active = toNumber(thumb.dataset.n390MediaTarget) === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', String(active));
        if (active) {
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        }
      });
    }

    activateMedia(index, shouldScroll = true) {
      if (!this.mediaSlides.length) return;
      const nextIndex = (index + this.mediaSlides.length) % this.mediaSlides.length;
      this.setMediaState(nextIndex);

      if (shouldScroll && this.mediaTrack) {
        this.mediaTrack.scrollTo({
          left: this.mediaSlides[nextIndex].offsetLeft,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }
    }

    handleMediaScroll() {
      if (!this.mediaTrack || this.mediaScrollFrame) return;
      this.mediaScrollFrame = requestAnimationFrame(() => {
        this.mediaScrollFrame = null;
        const trackLeft = this.mediaTrack.getBoundingClientRect().left;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.mediaSlides.forEach((slide, index) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
          if (distance < closestDistance) {
            closestIndex = index;
            closestDistance = distance;
          }
        });

        if (closestIndex !== this.mediaIndex) this.setMediaState(closestIndex);
      });
    }

    handleReviewScroll() {
      if (!this.reviewTrack || this.reviewScrollFrame) return;
      this.reviewScrollFrame = requestAnimationFrame(() => {
        this.reviewScrollFrame = null;
        const trackLeft = this.reviewTrack.getBoundingClientRect().left;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.reviewSlides.forEach((slide, index) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
          if (distance < closestDistance) {
            closestIndex = index;
            closestDistance = distance;
          }
        });

        this.setReviewState(closestIndex);
      });
    }

    setReviewState(index) {
      this.reviewIndex = index;
      this.reviewSlides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.reviewDots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });
    }

    activateReview(index, shouldScroll = true) {
      if (!this.reviewSlides.length) return;
      const nextIndex = (index + this.reviewSlides.length) % this.reviewSlides.length;
      this.setReviewState(nextIndex);
      if (shouldScroll && this.reviewTrack) {
        this.reviewTrack.scrollTo({
          left: this.reviewSlides[nextIndex].offsetLeft,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }
    }

    updateOffer() {
      const tier = this.selectedTier;
      if (!tier) return;

      this.tiers.forEach((item) => {
        const checked = Boolean(item.querySelector('input[type="radio"]')?.checked);
        item.classList.toggle('is-selected', checked);
      });

      const refill = this.querySelector('[data-n390-refill]');
      const canSubscribe = Boolean(tier.dataset.planId);
      let subscribed = this.refillEnabled && canSubscribe;

      if (refill) {
        refill.disabled = !canSubscribe;
        if (!canSubscribe) {
          refill.setAttribute('aria-pressed', 'false');
          subscribed = false;
        }
      }

      this.tiers.forEach((item) => {
        const itemSubscribed = subscribed && Boolean(item.dataset.planId);
        const price = itemSubscribed ? item.dataset.subTotal : item.dataset.oneTotal;
        const daily = itemSubscribed ? item.dataset.subDaily : item.dataset.oneDaily;
        const save = itemSubscribed ? item.dataset.subSave : item.dataset.oneSave;
        const priceNode = item.querySelector('[data-n390-price]');
        const dailyNode = item.querySelector('[data-n390-daily]');
        const saveNode = item.querySelector('[data-n390-save]');

        if (priceNode) priceNode.textContent = price || '';
        if (dailyNode) dailyNode.textContent = daily || '';
        if (saveNode) saveNode.textContent = save || '';
      });

      const sold = Math.min(100, Math.max(0, toNumber(tier.dataset.sold)));
      const stockBar = this.querySelector('[data-n390-stock-bar]');
      const stockCopy = this.querySelector('[data-n390-stock-copy]');
      if (stockBar && sold) stockBar.style.width = `${sold}%`;
      if (stockCopy && sold) {
        const label = stockCopy.textContent.replace(/^\d+%\s*/, '');
        stockCopy.textContent = `${sold}% ${label}`;
      }
    }

    getCartSections() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.getSectionsToRender !== 'function') return [];
      return drawer.getSectionsToRender().map((section) => section.id).filter(Boolean);
    }

    async addToCart() {
      const button = this.querySelector('[data-n390-atc]');
      const label = button?.querySelector('[data-n390-atc-label]');
      const tier = this.selectedTier;
      const variantId = toNumber(this.dataset.variantId);
      if (!button || !label || !tier || !variantId || button.disabled) return;

      const quantity = Math.max(1, toNumber(tier.dataset.quantity, 1));
      const planId = toNumber(tier.dataset.planId);
      const subscribed = this.refillEnabled && planId > 0;
      const originalLabel = label.textContent;

      button.disabled = true;
      button.dataset.state = 'loading';
      label.textContent = button.dataset.loadingLabel || 'Adding...';

      try {
        const payload = {
          id: variantId,
          quantity
        };
        if (subscribed) payload.selling_plan = planId;

        const sections = this.getCartSections();
        if (sections.length) {
          payload.sections = sections;
          payload.sections_url = window.location.pathname;
        }

        const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(payload)
        });

        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(result.description || result.message || 'Cart add failed');
        }

        button.dataset.state = 'success';
        label.textContent = button.dataset.successLabel || 'Added to cart';
        document.dispatchEvent(new CustomEvent('cart:add', {
          bubbles: true,
          detail: { item: result, source: ELEMENT_NAME }
        }));
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { item: result, source: ELEMENT_NAME }
        }));
        openCart(result, button, this.dataset.cartUrl);

        window.setTimeout(() => {
          button.dataset.state = '';
          button.disabled = false;
          label.textContent = button.dataset.defaultLabel || originalLabel;
        }, 1400);
      } catch (error) {
        console.error('[nuora-pdp-390] Add to cart failed', error);
        button.dataset.state = 'error';
        label.textContent = button.dataset.errorLabel || 'Try again';
        window.setTimeout(() => {
          button.dataset.state = '';
          button.disabled = false;
          label.textContent = button.dataset.defaultLabel || originalLabel;
        }, 1800);
      }
    }

    get nutritionModal() {
      return this.querySelector('[data-n390-nutrition-modal]');
    }

    openNutrition() {
      const dialog = this.nutritionModal;
      if (!dialog) return;
      this.nutritionReturnFocus = this.querySelector('[data-n390-nutrition-open]');
      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      dialog.addEventListener('click', this.handleDialogBackdrop, { once: true });
    }

    closeNutrition() {
      const dialog = this.nutritionModal;
      if (!dialog) return;
      if (typeof dialog.close === 'function' && dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        this.restoreNutritionFocus();
      }
    }

    restoreNutritionFocus() {
      const trigger = this.nutritionReturnFocus;
      this.nutritionReturnFocus = null;
      if (!trigger) return;

      if (this.lastInputMethod === 'keyboard') {
        trigger.focus({ preventScroll: true });
        return;
      }

      requestAnimationFrame(() => trigger.blur());
    }

    handleDialogBackdrop = (event) => {
      if (event.target === event.currentTarget) {
        this.closeNutrition();
      } else {
        event.currentTarget.addEventListener('click', this.handleDialogBackdrop, { once: true });
      }
    };
  }

  customElements.define(ELEMENT_NAME, NuoraPdp390);
})();
